import {HttpClient}                                                                                        from '@angular/common/http';
import {Component}                                                                                         from '@angular/core';
import {FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {MatSnackBar}                                                                                       from '@angular/material/snack-bar';
import {ActivatedRoute}                                                                                    from '@angular/router';
import {User}                                                                                              from '@src/app/admin/users/models/user.model';
import {UsersService}                                                                                      from '@src/app/admin/users/services/users.service';
import {AuthService}                                                                                       from '@src/app/core/services/auth.service';
import {ApiService}                                                                                        from '@src/app/shared/services/api.service';
import { WalkthroughService } from '@src/app/shared/services/walkthrough/walkthrough.service';
import {AddressType}                                                                                       from '../../models/addresses.model';
import {OrganisationService}                                                                               from '../../services/organisation.service';

@Component({
    selector: 'app-organisation-settings',
    templateUrl: './organisation-settings.component.html',
    styleUrls: ['./organisation-settings.component.scss']
})
export class OrganisationSettingsComponent
{
    public organisationForm: FormGroup;
    public submitted: boolean = false;
    public systemUsers: User;
    public imagePreview: any = null;
    public add_system_owners = [];
    public remove_system_owners = [];
    public countries = [];
    public tradingCountrySearch = [];
    public billingCountrySearch = [];
    public defaultCountry = {
        'flags': {
            'png': 'https://flagcdn.com/w320/gb.png',
            'svg': 'https://flagcdn.com/gb.svg',
            'alt': 'The flag of the United Kingdom — the Union Jack — has a blue field. It features the white-edged red cross of Saint George superimposed on the diagonal red cross of Saint Patrick which is superimposed on the diagonal white cross of Saint Andrew.'
        },
        'name': {
            'common': 'United Kingdom',
            'official': 'United Kingdom of Great Britain and Northern Ireland',
            'nativeName': {
                'eng': {
                    'official': 'United Kingdom of Great Britain and Northern Ireland',
                    'common': 'United Kingdom'
                }
            }
        },
        'cca2': 'GB'
    };

    get f()
    {
        return this.organisationForm.controls;
    }

    get system_owners()
    {
        return this.f['system_owners'] as UntypedFormArray;
    }

    constructor(
        public walkthroughService: WalkthroughService,
        private fb: UntypedFormBuilder,
        private userService: UsersService,
        private organisationService: OrganisationService,
        private authService: AuthService,
        private snackBar: MatSnackBar,
        private apiService: ApiService,
        private activatedRoute: ActivatedRoute,
        private httpClient: HttpClient
    )
    {
        this.userService.list(1).subscribe(response => {
            this.systemUsers = response.data;
        }, error => this.userService.ErrorHandler(error));

        this.walkthroughService.start('settings.company', false);

        this.countries.push(this.defaultCountry);

        this.activatedRoute.data.subscribe(
            response => {
                let responseData = response.organisation.data;
                this.imagePreview = responseData.logo_url ?? null;
                this.organisationForm = new UntypedFormGroup({
                    name: new UntypedFormControl(responseData.name ?? null, Validators.required),
                    billing_email: new UntypedFormControl(responseData.billing_email ?? null, [Validators.required, Validators.email]),
                    logo: new UntypedFormControl(null, Validators.required),
                    system_owners: new UntypedFormArray([], Validators.required),
                    add_system_owner: new UntypedFormControl(null),
                    trading_address: new UntypedFormGroup({
                        address_id: new UntypedFormControl(responseData.trading_address.id ?? null),
                        type_id: new UntypedFormControl(AddressType.TradingAddress ?? null),
                        title: new UntypedFormControl('Trading Address'),
                        address_line_1: new UntypedFormControl(responseData.trading_address.address_line_1 ?? null, Validators.required),
                        address_line_2: new UntypedFormControl(responseData.trading_address.address_line_2 ?? null),
                        address_line_3: new UntypedFormControl(responseData.trading_address.address_line_3 ?? null),
                        locality: new UntypedFormControl(responseData.trading_address.locality ?? null),
                        county: new UntypedFormControl(responseData.trading_address.county ?? null),
                        country: new UntypedFormControl(responseData.trading_address.country ?? null, Validators.required),
                        postcode: new UntypedFormControl(responseData.trading_address.postcode ?? null, Validators.required),
                    }),
                    billing_address: new UntypedFormGroup({
                        address_id: new UntypedFormControl(responseData.billing_address.id ?? null),
                        type_id: new UntypedFormControl(AddressType.BillingAddress ?? null),
                        title: new UntypedFormControl('Billing Address'),
                        address_line_1: new UntypedFormControl(responseData.billing_address.address_line_1 ?? null, Validators.required),
                        address_line_2: new UntypedFormControl(responseData.billing_address.address_line_2 ?? null),
                        address_line_3: new UntypedFormControl(responseData.billing_address.address_line_3 ?? null),
                        locality: new UntypedFormControl(responseData.billing_address.locality ?? null),
                        county: new UntypedFormControl(responseData.billing_address.county ?? null),
                        country: new UntypedFormControl(responseData.billing_address.country ?? null, Validators.required),
                        postcode: new UntypedFormControl(responseData.billing_address.postcode ?? null, Validators.required),
                    }),
                });

                this.httpClient.get('https://restcountries.com/v3.1/all?fields=name,flags,cca2').subscribe(
                    (res: any) => {
                        this.countries = res;
                        const gbCountryBilling = this.countries.find(country => country.cca2 === responseData.billing_address.country_code);
                        const gbCountryTrading = this.countries.find(country => country.cca2 === responseData.trading_address.country_code);
                        if (gbCountryBilling && gbCountryTrading) {
                            this.f.trading_address.get('country').setValue(gbCountryTrading);
                            this.f.billing_address.get('country').setValue(gbCountryBilling);
                        }
                        this.tradingCountrySearch = this.countries;
                        this.billingCountrySearch = this.countries;
                    },
                    error => {
                        this.countries.push(this.defaultCountry);
                        this.tradingCountrySearch = this.countries;
                        this.billingCountrySearch = this.countries;
                    }
                );

                for (let owner of responseData.system_owners) {
                    this.addSystemOwner(false, owner);
                }

                for (let [index, owner] of this.system_owners.getRawValue().entries()) {
                    if (owner.id == this.userService.user.id || this.system_owners.getRawValue().length == 1) {
                        this.system_owners.controls[index].disable();
                    }
                }
            },
            error => {
                this.apiService.handleError(error);
            }
        );
    }

    public submitForm(): void
    {
        if (this.f.name.valid) {
            let formData = new FormData();

            formData.append('name', this.f.name.value);
            formData.append('billing_email', this.f.billing_email.value);

            if (!(typeof this.f.logo.value === 'string') && this.f.logo.value !== null) {
                formData.append('logo', this.f.logo.value);
            }

            for (let owner of this.add_system_owners.map(so => so?.id ?? so)) {
                formData.append('add_system_owner_ids[]', owner);
            }

            for (let owner of this.remove_system_owners.map(so => so?.id ?? so)) {
                formData.append('remove_system_owner_ids[]', owner);
            }

            this.organisationService.editOrganisation(this.authService.tenant?.id, formData).subscribe(
                response => this.snackBar.open('Organisation settings saved successfully.', 'Ok.'),
                error => this.snackBar.open('There was a problem updating the settings.', 'Ok.')
            );

            this.organisationService.editAddress(this.f.billing_address.value.address_id, this.f.billing_address.value).subscribe(
                response => this.snackBar.open('Address saved successfully.', 'Ok.'),
                error => this.snackBar.open('There was a problem updating the address.', 'Ok.')
            );

            this.organisationService.editAddress(this.f.trading_address.value.address_id, this.f.trading_address.value).subscribe(
                response => this.snackBar.open('Address saved successfully.', 'Ok.'),
                error => this.snackBar.open('There was a problem updating the address.', 'Ok.'),
            );
        }
    }

    public onDragDropFileChange(files: File[]): void
    {
        const mockEvent = { target: { files } } as unknown as Event;
        this.onFileChange(mockEvent);
    }

    public onFileChange(event: Event): void
    {
        const input = event.target as HTMLInputElement;

        this.f.logo.setValue(input.files[0]);

        let reader = new FileReader();

        reader.onload = (event: any) => {
            this.imagePreview = event.target.result;
        };

        reader.readAsDataURL(input.files[0]);
    }

    removeFile()
    {
        this.f.logo.setValue(null);
        this.imagePreview = null;
    }

    sameAsTradingAddress(event)
    {
        if (event.checked) {
            this.f.billing_address.setValue(this.f.trading_address.value);
        }
    }

    addSystemOwner(newOwner: boolean = false, owner_id?)
    {
        const system_owner = this.fb.group({
            id: [owner_id ?? this.organisationForm.controls.add_system_owner.value, Validators.required],
            name: ['beginner', Validators.required]
        });

        this.system_owners.push(system_owner);
        if (newOwner) {
            this.add_system_owners.push(owner_id ?? this.organisationForm.controls.add_system_owner.value);
            this.organisationForm.controls.add_system_owner.setValue(null);
        }
    }

    removeSystemOwner(ownerIndex: number)
    {
        let ownerVal = this.system_owners.controls[ownerIndex].value.id;

        this.system_owners.removeAt(ownerIndex);

        let index = this.add_system_owners.findIndex(c => c == ownerVal);
        index >= 0 ? this.add_system_owners.splice(index, 1) : this.remove_system_owners.push(ownerVal);
    }

    public forceUppercaseConditionally(formGroup, formControlName, event)
    {
        this.organisationForm.get(`${formGroup}.${formControlName}`).setValue(event.target.value.toUpperCase());
    }

    public searchTradingCountries(event: Event): void
    {
        const input = event.target as HTMLInputElement;
        input.value != '' ? this.tradingCountrySearch = this.countries.filter(c => c.name.common.toLowerCase().includes(input.value.toLowerCase())) : this.tradingCountrySearch = this.countries;
    }

    public searchBillingCountries(event: Event): void
    {
        const input = event.target as HTMLInputElement;
        input.value != '' ? this.billingCountrySearch = this.countries.filter(c => c.name.common.toLowerCase().includes(input.value.toLowerCase())) : this.billingCountrySearch = this.countries;
    }
}
